/**
 * Library landing (frontend-map §1.3 — LIBRARY-UI owned, T08/T10). Two tabs:
 *
 *  - "Owned by me": the caller's Master Lists via `useMasterLists()`, with a
 *    "New Master List" action (CreateMasterListDialog) and per-card Duplicate.
 *  - "Shared with me": lists other owners have shared with the caller. These
 *    open read-only in the UI and can be duplicated into an owned copy.
 */
import { useMasterLists, useSharedMasterLists } from '@/api/masterLists';
import { CreateMasterListDialog } from '@/components/library/CreateMasterListDialog';
import { MasterListGrid } from '@/components/library/MasterListGrid';
import { Tabs } from '@/components/library/ui/Tabs';
import { Button } from '@/components/ui/button';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import { createFileRoute } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/library/')({
  component: LibraryIndex,
});

type LibraryTab = 'owned' | 'shared';

function LibraryIndex() {
  useDocumentTitle('Library');
  const [tab, setTab] = useState<LibraryTab>('owned');
  const [createOpen, setCreateOpen] = useState(false);
  const masterLists = useMasterLists();
  const sharedLists = useSharedMasterLists();

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold">Library</h1>
          <p className="text-sm text-[var(--muted-foreground)]">
            Reusable Master Lists you can pull into any trip.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="self-start sm:self-auto">
          <Plus />
          New Master List
        </Button>
      </div>

      <Tabs
        value={tab}
        onValueChange={(value) => setTab(value as LibraryTab)}
        tabs={[
          { value: 'owned', label: 'Owned by me' },
          { value: 'shared', label: 'Shared with me' },
        ]}
      />

      {tab === 'owned' ? (
        <MasterListGrid
          lists={masterLists.data}
          isLoading={masterLists.isLoading}
          isError={masterLists.isError}
          onCreate={() => setCreateOpen(true)}
        />
      ) : (
        <MasterListGrid
          lists={sharedLists.data}
          isLoading={sharedLists.isLoading}
          isError={sharedLists.isError}
          onCreate={() => setCreateOpen(true)}
          emptyLabel="Nothing shared with you yet."
          showCreate={false}
        />
      )}

      <CreateMasterListDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}
